/**
 * ADJUNTOS DEL CHAT ELITE (cliente)
 * Validacion previa para avisar al usuario al momento y compresion de fotos antes de subir.
 * El servidor vuelve a validar todo (contenido real, limites) y re-procesa las imagenes.
 */

// Mismos limites que el back (constants/chatElite.js -> ADJUNTOS)
export const ADJUNTOS_LIMITES = {
  TAMANO_MAX: 10 * 1024 * 1024,
  POR_MENSAJE: 5,
  TOTAL_POR_MENSAJE: 20 * 1024 * 1024,
};

const IMAGENES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];

// Documentos permitidos (el back valida el contenido real y rechaza los que tienen macros)
const EXTENSIONES_DOCUMENTO = ["pdf", "doc", "docx", "odt", "txt", "xls", "xlsx", "ods", "csv"];
const HOJAS = ["xls", "xlsx", "ods", "csv"];

export const TIPOS_ACEPTADOS = [
  ...IMAGENES,
  ...EXTENSIONES_DOCUMENTO.map((e) => `.${e}`),
].join(",");

const extension = (nombre) => String(nombre || "").split(".").pop().toLowerCase();

/**
 * Tipo de un adjunto para pintarlo: "imagen", "pdf", "hoja" (Excel, CSV...) o "documento" (Word, TXT...).
 * Solo imagenes y PDF se abren en el navegador; el resto se descarga.
 */
export const tipoAdjunto = (mime, nombre) => {
  if ((mime || "").startsWith("image/")) return "imagen";
  const ext = extension(nombre);
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (HOJAS.includes(ext) || /spreadsheet|excel|csv/.test(mime || "")) return "hoja";
  return "documento";
};

export const etiquetaExtension = (nombre) => extension(nombre).toUpperCase();
const LADO_MAX = 2560;
const COMPRIMIR_DESDE = 1.5 * 1024 * 1024;

export const esImagen = (mime) => (mime || "").startsWith("image/");

export const formatearTamano = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Filtra los ficheros seleccionados. Devuelve los validos y los errores (codigos i18n) de los rechazados.
 * @param {File[]} nuevos
 * @param {number} yaSeleccionados
 * @returns {{ validos: File[], errores: Array<{ nombre: string, codigo: string }> }}
 */
export const validarArchivos = (nuevos, yaSeleccionados = 0) => {
  const validos = [];
  const errores = [];
  for (const archivo of nuevos) {
    // Algunos navegadores no dan mimetype para CSV u Office: se mira tambien la extension
    const tipoOk = IMAGENES.includes(archivo.type) || EXTENSIONES_DOCUMENTO.includes(extension(archivo.name));
    if (!tipoOk) errores.push({ nombre: archivo.name, codigo: "ADJUNTO_TIPO_NO_PERMITIDO" });
    else if (archivo.size > ADJUNTOS_LIMITES.TAMANO_MAX) errores.push({ nombre: archivo.name, codigo: "ADJUNTO_DEMASIADO_GRANDE" });
    else if (yaSeleccionados + validos.length >= ADJUNTOS_LIMITES.POR_MENSAJE) errores.push({ nombre: archivo.name, codigo: "DEMASIADOS_ADJUNTOS" });
    else validos.push(archivo);
  }
  return { validos, errores };
};

/**
 * Reduce fotos grandes (JPEG/WebP) a 2560 px y calidad 0.85 antes de subir.
 * PNG (capturas), GIF y HEIC se envian tal cual; si algo falla se envia el original.
 */
export const comprimirImagen = async (archivo) => {
  if (!["image/jpeg", "image/webp"].includes(archivo.type) || archivo.size < COMPRIMIR_DESDE) return archivo;
  if (typeof createImageBitmap !== "function") return archivo;

  try {
    const bitmap = await createImageBitmap(archivo, { imageOrientation: "from-image" });
    const escala = Math.min(1, LADO_MAX / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * escala);
    canvas.height = Math.round(bitmap.height * escala);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob || blob.size >= archivo.size) return archivo;
    const nombre = archivo.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], nombre, { type: "image/jpeg", lastModified: archivo.lastModified });
  } catch (_) {
    return archivo;
  }
};

/**
 * Comprime las fotos y comprueba el total del mensaje (tras comprimir: 5 fotos de movil de 6 MB
 * suelen quedar en ~3 MB). Lanza { code: "MENSAJE_DEMASIADO_GRANDE" } si no cabe.
 */
export const prepararArchivos = async (archivos) => {
  const preparados = await Promise.all(archivos.map(comprimirImagen));
  const total = preparados.reduce((suma, a) => suma + a.size, 0);
  if (total > ADJUNTOS_LIMITES.TOTAL_POR_MENSAJE) {
    const error = new Error("MENSAJE_DEMASIADO_GRANDE");
    error.code = "MENSAJE_DEMASIADO_GRANDE";
    error.total = total;
    throw error;
  }
  return preparados;
};
